import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://placeholder.supabase.co';
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || 'placeholder-key';

// Check if we have real Supabase credentials
export const hasSupabaseCredentials = 
  supabaseUrl !== 'https://placeholder.supabase.co' && 
  supabaseAnonKey !== 'placeholder-key' &&
  supabaseUrl.includes('supabase.co') &&
  supabaseUrl.startsWith('https://') &&
  supabaseAnonKey.length > 50; // Supabase keys are typically longer

// Create client with enhanced error handling
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
  },
  global: {
    fetch: async (url, options = {}) => {
      try {
        // Add timeout to prevent hanging requests
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000);
        
        const response = await fetch(url, {
          ...options,
          signal: controller.signal,
        });
        
        clearTimeout(timeoutId);
        
        // Handle status code 530 specifically
        if (response.status === 530) {
          console.warn('Supabase returned status 530 - likely configuration issue');
          throw new Error('Supabase connection rejected (530)');
        }
        
        return response;
      } catch (error) {
        if (error.name === 'AbortError') {
          throw new Error('Request timeout');
        }
        throw error;
      }
    },
  },
});

// Log connection status for debugging (only in development)
if (typeof window !== 'undefined' && import.meta.env.DEV) {
  console.log('Supabase connection status:', {
    hasCredentials: hasSupabaseCredentials,
    url: supabaseUrl.substring(0, 30) + '...',
    keyLength: supabaseAnonKey.length,
    environment: import.meta.env.MODE
  });
  
  // Make supabase available for debugging
  (window as any).supabase = supabase;
}

// Enhanced error handling for Supabase operations
export const safeSupabaseOperation = async <T>(
  operation: () => Promise<{ data: T; error: any }>,
  fallback?: T
): Promise<{ data: T | null; error: any; usingFallback: boolean }> => {
  if (!hasSupabaseCredentials) {
    console.warn('Supabase credentials not configured, using fallback data');
    return {
      data: fallback || null,
      error: null,
      usingFallback: true
    };
  }

  try {
    const result = await Promise.race([
      operation(),
      new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Operation timeout')), 15000)
      )
    ]) as { data: T; error: any };

    if (result.error) {
      console.warn('Supabase operation returned error:', result.error);
      
      // Handle specific error cases
      if (result.error.message?.includes('530') || 
          result.error.message?.includes('connection rejected') ||
          result.error.message?.includes('timeout')) {
        return {
          data: fallback || null,
          error: result.error,
          usingFallback: true
        };
      }
    }

    return {
      data: result.data,
      error: result.error,
      usingFallback: false
    };
  } catch (error) {
    console.warn('Supabase operation failed, using fallback:', error);
    return {
      data: fallback || null,
      error,
      usingFallback: true
    };
  }
};

// Connection test function
export const testSupabaseConnection = async (): Promise<boolean> => {
  if (!hasSupabaseCredentials) {
    return false;
  }

  try {
    const { error } = await supabase.rpc('get_published_blog_posts').limit(1);
    return !error;
  } catch (error) {
    console.warn('Supabase connection test failed:', error);
    return false;
  }
};